'use client'
import React, { useCallback } from 'react'
import { type FC, ReactNode, memo, useEffect, useState, useRef } from 'react'
import Flickity, { type ReactFlickityComponentProps } from 'react-flickity-component'
import type FlickityInstance from 'flickity'
import { useDeviceType } from '@/hooks'

type Props = {
  children: ReactNode,
  className?: string,
  reloadOnUpdate?: boolean,
  dynamicSlider?: boolean,
  elementType?: 'div' | 'section' | 'aside' | 'dd' | 'ul',
  options?: ReactFlickityComponentProps['options'],
  onChange?: (params: unknown) => void,
  hasCustomDots?: boolean
  colorDots?: 'white' | '#3C3C3B'
}

const defaultOptions:ReactFlickityComponentProps['options'] = {
  accessibility: false,
  autoPlay: false,
  cellAlign: 'left',
  contain: true,
  draggable: true,
  dragThreshold: 5,
  freeScroll: true,
  freeScrollFriction: 0.075,
  friction: 0.25,
  groupCells: false,
  initialIndex: 0,
  lazyLoad: 2,
  pauseAutoPlayOnHover: true,
  prevNextButtons: false,
  pageDots: false,
  resize: true,
  selectedAttraction: 0.01,
  wrapAround: false
}

const sliderBeingDragged = 'slider-being-dragged'
let originalTouchMoveHandler: ((e: TouchEvent) => boolean) | null = null

function dragEnd() {
  if (originalTouchMoveHandler !== null) {
    document.ontouchmove = originalTouchMoveHandler
  } else {
    document.ontouchmove = null
  }
  setTimeout(() => {
    document.body.classList.remove(sliderBeingDragged)
  }, 50)
}

function dragMove() {
  if (originalTouchMoveHandler === null) {
    originalTouchMoveHandler = document.ontouchmove
  }
  document.ontouchmove = () => false
  document.body.classList.add(sliderBeingDragged)
}

function dragStart() {
  if (originalTouchMoveHandler === null) {
    originalTouchMoveHandler = document.ontouchmove
  }
  document.ontouchmove = () => false
}

export const SliderFlickity: FC<Props> = memo(({ children, className = '', elementType = 'div', options = {}, onChange = null, reloadOnUpdate = false, dynamicSlider = false, hasCustomDots = false, colorDots = 'white' }) => {
  const [flickityRef, setFlickityRef] = useState<FlickityInstance | null>(null)
  const dotsContainerRef = useRef<HTMLDivElement>(null)
  const { isMobile } = useDeviceType()

  // NEW: Pure function that only updates visual state
  const updateDots = useCallback(() => {
    const dots = dotsContainerRef.current?.children
    if (!dots || !flickityRef) return

    for (let i = 0; i < dots.length; i++) {
      const lines = dots[i].querySelectorAll('line')
      const progressLine = lines[1]
      const isSelected = i === flickityRef.selectedIndex

      if (isSelected) {
        progressLine.classList.remove('viewed')
        progressLine.style.strokeOpacity = '1'
        progressLine.style.transition = `stroke-dasharray ${flickityRef.options.autoPlay || 4000}ms linear`
        progressLine.style.strokeDasharray = '62, 62'
      }

      if (dots[i].classList.contains('viewed')) {
        progressLine.style.strokeOpacity = '1'
        progressLine.style.strokeDasharray = '62, 62'
        progressLine.style.transition = 'none'
      }

      if (!isSelected && !dots[i].classList.contains('viewed')) {
        progressLine.style.strokeOpacity = '.4'
        progressLine.style.strokeDasharray = '0, 62'
      }
    }
  }, [flickityRef])

  const handleSlideChange = (currentIndex: number) => {
    const dots = dotsContainerRef.current?.children
    if (!dots) return

    const prevIndex = currentIndex === 0 ? dots.length - 1 : currentIndex - 1

    if (dots[prevIndex]) {
      dots[prevIndex].classList.add('viewed')
      const lines = dots[prevIndex].querySelectorAll('line')
      const progressLine = lines[1]
      progressLine.style.strokeOpacity = '1'
      progressLine.style.strokeDasharray = '62, 62'
      progressLine.style.transition = 'none'
    }

    if (currentIndex === 0) {
      // Reset all dots
      for (let i = 0; i < dots.length; i++) {
        dots[i].classList.remove('viewed')
        const lines = dots[i].querySelectorAll('line')
        const progressLine = lines[1]
        progressLine.style.transition = 'none'
        progressLine.style.strokeDasharray = '0, 62'
        progressLine.style.strokeOpacity = '.4'
      }

      requestAnimationFrame(() => {
        updateDots()
      })
    }
  }


  const handleDotClick = (index: number) => {
    if (flickityRef) {
      flickityRef.select(index)
      if (flickityRef.options.autoPlay) {
        flickityRef.stopPlayer()
        flickityRef.playPlayer()
      }
    }
  }

  useEffect(() => {
    if (!flickityRef) return

    flickityRef.on('dragStart', dragStart)
    flickityRef.on('dragMove', dragMove)
    flickityRef.on('dragEnd', dragEnd)
    if (onChange) flickityRef.on('change', onChange)

    if (hasCustomDots) {
      flickityRef.on('select', updateDots)
      flickityRef.on('change', handleSlideChange)
      updateDots()
    }

    return () => {
      dragEnd()
      flickityRef.off('dragStart', dragStart)
      flickityRef.off('dragMove', dragMove)
      flickityRef.off('dragEnd', dragEnd)
      if (hasCustomDots) {
        flickityRef.off('select', updateDots)
        flickityRef.off('change', handleSlideChange)
      }
      if (onChange) flickityRef.off('change', onChange)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flickityRef, hasCustomDots, onChange])


  useEffect(() => {
    if (!flickityRef || isMobile || options.draggable) return

    const flickityElement = flickityRef.element
    const nextOnClick = () => {
      const nextIndex = (flickityRef.selectedIndex + 1) % flickityRef.slides.length
      flickityRef.select(nextIndex)

      if (flickityRef.options.autoPlay) {
        flickityRef.stopPlayer()
        flickityRef.playPlayer()
      }

      if (hasCustomDots) {
        updateDots()
      }
    }

    flickityElement.style.cursor = 'pointer'
    flickityElement.addEventListener('click', nextOnClick)

    return () => {
      flickityElement.removeEventListener('click', nextOnClick)
    }
  }, [flickityRef, hasCustomDots, isMobile, updateDots, options.draggable])

  return (
    <div className='slider-wrapper'>
      <Flickity
        flickityRef={(ref) => setFlickityRef(ref)}
        className={`slider ${className}`}
        elementType={elementType}
        options={{ ...defaultOptions, ...options }}
        disableImagesLoaded={false}
        reloadOnUpdate={reloadOnUpdate}
        static={!dynamicSlider}
      >
        {children}
      </Flickity>
      {hasCustomDots && <div className='custom-dots' ref={dotsContainerRef}>
        {React.Children.map(children, (_, i) => (
          <svg key={i} xmlns='http://www.w3.org/2000/svg' width='62' height='3' viewBox='0 0 62 1.5' fill='none' onClick={() => handleDotClick(i)} style={{ cursor: 'pointer' }} >
            <line y1='1.5' x2='62' y2='1.5' stroke={colorDots === 'white' ? colorDots : '#3C3C3B'} strokeOpacity='0.4' strokeWidth='2' />
            <line y1='1.5' x2='62' y2='1.5' stroke={colorDots === 'white' ? colorDots : '#3C3C3B'} strokeWidth='2' strokeDasharray='0, 62' />
          </svg>
        ))}
      </div>}
    </div>
  )
})
